import { GetLocation, GetLocationCohort } from 'src/graphql/types/delivery';
import {
  GCustomerLocationMetrobiEligibilityFilter,
  GCustomerLocationRoutingFilter,
  GCustomerLocationsInput,
} from 'src/graphql/types/customerLocationFilters.generated';

export enum RoutingFilter {
  allLocations = 'allLocations',
  automaticallyRouted = 'automaticallyRouted',
  manuallyRouted = 'manuallyRouted',
}

export enum MetrobiFilter {
  All = 'all',
  Eligible = 'eligible',
  Ineligible = 'ineligible',
}

export const metrobiFilterItems = [
  { label: 'All Metrobi Eligibility', value: MetrobiFilter.All },
  { label: 'Eligible', value: MetrobiFilter.Eligible },
  { label: 'Ineligible', value: MetrobiFilter.Ineligible },
];

export enum DeliveryWindowFilter {
  allWindows = 'allWindows',
  nightTime = 'nightTime',
  dayTime = 'dayTime',
}

export type Item = {
  value: string;
  label: string;
};

export const routingFilterItems = [
  { label: 'All Locations', value: RoutingFilter.allLocations },
  { label: 'Automatically Routed', value: RoutingFilter.automaticallyRouted },
  { label: 'Manually Routed', value: RoutingFilter.manuallyRouted },
];

export const allStatesItem = {
  label: 'All States',
  value: 'allStates',
};

export const allCitiesItem = {
  label: 'All Cities',
  value: 'allCities',
};

export const allCohortsItem = {
  label: 'All Cohorts',
  value: 'allCohorts',
};

export const noCohortsItem = {
  label: 'No Cohorts',
  value: 'noCohorts',
};

export const uniqueItems = (
  value: Item,
  index: number,
  self: Item[],
): boolean => index === self.findIndex((t: Item) => t.value === value.value);

// Translates the table's dropdown/search state into the backend filter input (sc-67610).
// Undefined fields are dropped from the request, so "All …" selections send nothing.
export const buildCustomerLocationsInput = ({
  warehouseId,
  metrobiFilter = MetrobiFilter.All,
  searchValue,
  routingFilter,
  stateFilter,
  cityFilter,
  zipFilter,
  cohortFilter,
  cohorts,
}: {
  warehouseId?: string;
  metrobiFilter?: MetrobiFilter;
  searchValue: string;
  routingFilter: RoutingFilter;
  stateFilter: string;
  cityFilter: string;
  zipFilter: string;
  cohortFilter: string;
  cohorts?: GetLocationCohort[];
}): GCustomerLocationsInput => {
  const search = searchValue.trim();
  const zip = zipFilter.trim();
  const isCohortSelected =
    cohortFilter !== allCohortsItem.value &&
    cohortFilter !== noCohortsItem.value;
  return {
    warehouseId,
    search: search === '' ? undefined : search,
    routing: routingFilterInput[routingFilter],
    state: stateFilter === allStatesItem.value ? undefined : stateFilter,
    city: cityFilter === allCitiesItem.value ? undefined : cityFilter,
    zip: zip === '' ? undefined : zip,
    cohortId: isCohortSelected ? cohortFilter : undefined,
    // "No Cohorts" means the location's cohort is not one of the warehouse plan's cohorts.
    notInCohortIds:
      cohortFilter === noCohortsItem.value
        ? (cohorts ?? []).map((cohort) => cohort.id)
        : undefined,
    metrobiEligibility: metrobiFilterInput[metrobiFilter],
  };
};

// The server filters by stored values; fields edited on this page (routing, Metrobi,
// cohort) are overlaid locally until the next fetch. Re-checking them on loaded rows keeps
// an edited location from lingering under a filter it no longer matches.
export const matchesEditableFilters = (
  location: GetLocation,
  input: GCustomerLocationsInput,
): boolean => {
  if (
    input.routing === GCustomerLocationRoutingFilter.Automatic &&
    !location.includeInAutomatedRouting
  )
    return false;
  if (
    input.routing === GCustomerLocationRoutingFilter.Manual &&
    location.includeInAutomatedRouting
  )
    return false;
  if (
    input.metrobiEligibility ===
      GCustomerLocationMetrobiEligibilityFilter.Eligible &&
    location.effectivelyExcluded !== false
  )
    return false;
  if (
    input.metrobiEligibility ===
      GCustomerLocationMetrobiEligibilityFilter.Excluded &&
    location.effectivelyExcluded !== true
  )
    return false;
  if (input.cohortId && location.routingCohort?.id !== input.cohortId)
    return false;
  if (
    input.notInCohortIds &&
    location.routingCohort &&
    input.notInCohortIds.includes(location.routingCohort.id)
  )
    return false;
  return true;
};

const routingFilterInput: Record<
  RoutingFilter,
  GCustomerLocationRoutingFilter | undefined
> = {
  [RoutingFilter.allLocations]: undefined,
  [RoutingFilter.automaticallyRouted]: GCustomerLocationRoutingFilter.Automatic,
  [RoutingFilter.manuallyRouted]: GCustomerLocationRoutingFilter.Manual,
};

const metrobiFilterInput: Record<
  MetrobiFilter,
  GCustomerLocationMetrobiEligibilityFilter | undefined
> = {
  [MetrobiFilter.All]: undefined,
  [MetrobiFilter.Eligible]: GCustomerLocationMetrobiEligibilityFilter.Eligible,
  [MetrobiFilter.Ineligible]:
    GCustomerLocationMetrobiEligibilityFilter.Excluded,
};

export const toItems = (values: string[]): Item[] =>
  values.map((value) => ({ value, label: value }));
